// Wraps any trading action button/page with a restriction-aware gate.
//
// Usage — wrap a button:
//   <ActionGate action="sell" config={tradingConfig}>
//     <button onClick={...}>Sell Crypto</button>
//   </ActionGate>
//
// Usage — wrap a whole page section:
//   <ActionGate action="buy" config={tradingConfig} fullPage>
//     <BuyForm />
//   </ActionGate>
//
// When blocked it shows an inline card or full-page block with the
// reason and a contextual CTA (e.g. "Complete KYC" or "Add Phone").

import { useNavigate } from "react-router-dom";

const ACTION_LABELS = {
  sell:            "Sell Crypto",
  buy:             "Buy Crypto",
  bill:            "Pay Bills",
  withdraw_crypto: "Withdraw Crypto",
  withdraw_naira:  "Withdraw Naira",
};

// Maps restriction codes to a helpful CTA
const CODE_CTA = {
  KYC_REQUIRED:       { label: "Complete KYC",     path: "/kyc" },
  KYC_PENDING:        { label: "View KYC Status",  path: "/kyc" },
  KYC_REJECTED:       { label: "Re-submit KYC",    path: "/kyc" },
  KYC_INFO_REQUESTED: { label: "Update KYC",       path: "/kyc" },
  PHONE_REQUIRED:     { label: "Add Phone Number", path: "/profile" },
  EMAIL_NOT_VERIFIED: { label: "Verify Email",     path: "/profile" },
  ACCOUNT_SUSPENDED:  { label: "Contact Support",  path: "/support" },
  DAILY_LIMIT_EXCEEDED: { label: "View Limits",    path: "/profile" },
};

const CODE_ICON = {
  KYC_REQUIRED:         "ri-shield-user-line",
  KYC_PENDING:          "ri-time-line",
  KYC_REJECTED:         "ri-close-circle-line",
  KYC_INFO_REQUESTED:   "ri-error-warning-line",
  PHONE_REQUIRED:       "ri-smartphone-line",
  EMAIL_NOT_VERIFIED:   "ri-mail-line",
  ACCOUNT_SUSPENDED:    "ri-forbid-line",
  MAINTENANCE:          "ri-tools-line",
  ACTION_DISABLED:      "ri-pause-circle-line",
  DAILY_LIMIT_EXCEEDED: "ri-bar-chart-line",
};

export default function ActionGate({ action, config, children, fullPage = false }) {
  const navigate = useNavigate();

  // While loading or if config is unavailable, render children normally
  if (!config || !config.actions) return children;

  const check = config.actions[action];
  if (!check || check.allowed) return children;

  const { code, message } = check;
  const cta  = CODE_CTA[code];
  const icon = CODE_ICON[code] || "ri-lock-line";
  const actionLabel = ACTION_LABELS[action] || action;

  // ── Full-page block (use inside a page body) ──────────────
  if (fullPage) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center">
        <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-5">
          <i className={`${icon} text-slate-400 text-3xl`} />
        </div>
        <h2 className="text-slate-800 font-bold text-xl mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          {actionLabel} Unavailable
        </h2>
        <p className="text-slate-500 text-sm leading-relaxed mb-8 max-w-xs">{message}</p>
        {cta && (
          <button
            onClick={() => navigate(cta.path)}
            className="px-8 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-2xl text-sm transition-colors"
          >
            {cta.label}
          </button>
        )}
      </div>
    );
  }

  // ── Inline card (use to replace a button or small section) ──
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-4 flex items-start gap-3">
      <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
        <i className={`${icon} text-amber-600 text-lg`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-amber-800 text-sm font-semibold mb-0.5">{actionLabel} Restricted</p>
        <p className="text-amber-700 text-xs leading-relaxed">{message}</p>
        {cta && (
          <button
            onClick={() => navigate(cta.path)}
            className="mt-2.5 text-xs font-bold text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
          >
            {cta.label} <i className="ri-arrow-right-s-line" />
          </button>
        )}
      </div>
    </div>
  );
}
